import React, { useEffect, useRef } from "react";
import "./CustomCursor.scss";

const INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "[role='button']",
  "label",
  "summary",
  "select",
  "[data-cursor]",
].join(", ");

const TEXT_SELECTOR = [
  "textarea",
  "[contenteditable='true']",
  "input:not([type='button']):not([type='submit']):not([type='reset']):not([type='checkbox']):not([type='radio']):not([type='range']):not([type='color'])",
].join(", ");

const DISABLED_SELECTOR = [
  "button:disabled",
  "input:disabled",
  "textarea:disabled",
  "select:disabled",
  "[aria-disabled='true']",
].join(", ");

const placeLayer = (layer, x, y) => {
  if (!layer) return;
  layer.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
};

const getCursorTarget = (target) => {
  if (!(target instanceof Element)) {
    return { mode: "default", label: "" };
  }

  const explicit = target.closest("[data-cursor]");
  const explicitLabel = target.closest("[data-cursor-label]");

  if (target.closest(DISABLED_SELECTOR)) {
    return { mode: "disabled", label: "" };
  }

  if (target.closest(TEXT_SELECTOR)) {
    return { mode: "text", label: "" };
  }

  if (explicit) {
    return {
      mode: explicit.getAttribute("data-cursor") || "interactive",
      label: explicitLabel?.getAttribute("data-cursor-label") || "",
    };
  }

  if (target.closest("a[href]")) {
    return { mode: "interactive", label: "Open" };
  }

  if (target.closest(INTERACTIVE_SELECTOR)) {
    return { mode: "interactive", label: "Tap" };
  }

  return { mode: "default", label: "" };
};

const CustomCursor = () => {
  const rootRef = useRef(null);
  const glowRef = useRef(null);
  const ringRef = useRef(null);
  const coreRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const glow = glowRef.current;
    const ring = ringRef.current;
    const core = coreRef.current;
    const label = labelRef.current;

    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!root || !glow || !ring || !core || !finePointer.matches) return undefined;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const documentElement = document.documentElement;

    let raf = 0;
    let hasEntered = false;
    let activeMode = "default";
    let activeLabel = "";

    const pointer = { x: -100, y: -100 };
    const trail = { x: -100, y: -100 };
    const haze = { x: -100, y: -100 };

    const setCursorMode = (target) => {
      const { mode, label: nextLabel } = getCursorTarget(target);

      if (mode !== activeMode) {
        root.classList.remove(`custom-cursor--${activeMode}`);
        root.classList.add(`custom-cursor--${mode}`);
        activeMode = mode;
      }

      if (label && nextLabel !== activeLabel) {
        label.textContent = nextLabel;
        activeLabel = nextLabel;
      }

      root.classList.toggle("custom-cursor--labeled", Boolean(nextLabel));
      if (!nextLabel) activeLabel = "";
    };

    const reveal = () => {
      if (!hasEntered) {
        trail.x = pointer.x;
        trail.y = pointer.y;
        haze.x = pointer.x;
        haze.y = pointer.y;
        hasEntered = true;
      }

      root.classList.add("custom-cursor--visible");
    };

    const hide = () => {
      root.classList.remove("custom-cursor--visible", "custom-cursor--pressed");
      hasEntered = false;
    };

    const onPointerMove = (event) => {
      if (event.pointerType && event.pointerType !== "mouse") return;

      pointer.x = event.clientX;
      pointer.y = event.clientY;
      setCursorMode(event.target);
      reveal();
    };

    const onPointerDown = () => root.classList.add("custom-cursor--pressed");
    const onPointerUp = () => root.classList.remove("custom-cursor--pressed");

    const onVisibilityChange = () => {
      if (document.hidden) hide();
    };

    const tick = () => {
      const trailEase = reducedMotion ? 1 : 0.2;
      const hazeEase = reducedMotion ? 1 : 0.08;

      trail.x += (pointer.x - trail.x) * trailEase;
      trail.y += (pointer.y - trail.y) * trailEase;
      haze.x += (pointer.x - haze.x) * hazeEase;
      haze.y += (pointer.y - haze.y) * hazeEase;

      placeLayer(core, pointer.x, pointer.y);
      placeLayer(ring, trail.x, trail.y);
      placeLayer(glow, haze.x, haze.y);

      raf = requestAnimationFrame(tick);
    };

    documentElement.classList.add("has-custom-cursor");
    root.classList.add("custom-cursor--default");

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("blur", hide);
    documentElement.addEventListener("mouseleave", hide);
    document.addEventListener("visibilitychange", onVisibilityChange);

    raf = requestAnimationFrame(tick);

    return () => {
      documentElement.classList.remove("has-custom-cursor");
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("blur", hide);
      documentElement.removeEventListener("mouseleave", hide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={rootRef} className="custom-cursor" aria-hidden="true">
      <span ref={glowRef} className="custom-cursor__glow" />
      <span ref={ringRef} className="custom-cursor__ring">
        <span ref={labelRef} className="custom-cursor__label" />
      </span>
      <span ref={coreRef} className="custom-cursor__core">
        <span className="custom-cursor__spark" />
      </span>
    </div>
  );
};

export default CustomCursor;
